import React, { useState } from 'react';
import { isEmpty } from 'ramda';
import { useParams } from 'react-router-dom';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { __ } from '@wordpress/i18n';

// store
import { useStoreValue, storeSet } from '../../../../../../store';

// helpers
import getDateFromISOstring from '../../../../../../helpers/getDateFromISOstring';

// components
import BadgeTrack from '../../../../../../components/BadgeTrack';
import ButtonIconSimple from '../../../../../../components/ButtonWrapper/components/ButtonIconSimple';
import LikeIcon from '../../../../../../icons/LikeIcon';
import ToReviewIcon from '../../../../../../icons/ToReviewIcon';
import DislikeIcon from '../../../../../../icons/DislikeIcon';
import TripleDotsIcon from '../../../../../../icons/TripleDotsIcon';
import PlusIcon from '../../../../../../icons/PlusIcon';
import ModalUploadSimple from '../../../../../../components/ModalUploadSimple';
import ModalUploadTrack from '../../../../../../components/ModalUploadTrack';
import ButtonIconContextMenu from '../../../../../../components/ButtonWrapper/components/ButtonIconContextMenu';
import RecLayerAssignButton from '../../../../../../components/RecLayerAssignButton';
import BassIcon from '../../../../../../icons/BassIcon';
import BrassIcon from '../../../../../../icons/BrassIcon';
import DrumsIcon from '../../../../../../icons/DrumsIcon';
import GuitarIcon from '../../../../../../icons/GuitarIcon';
import OtherInstrumentIcon from '../../../../../../icons/OtherInstrumentIcon';
import PercussionIcon from '../../../../../../icons/PercussionIcon';
import PianoIcon from '../../../../../../icons/PianoIcon';
import StringsIcon from '../../../../../../icons/StringsIcon';
import SynthIcon from '../../../../../../icons/SynthIcon';
import WoodwindIcon from '../../../../../../icons/WoodwindIcon';
import VolumeIcon from '../../../../../../icons/VolumeIcon';

import '../../../../../../assets/components/RecList.css';

const SESSION_TYPES = ['PIANO', 'GUITAR', 'BASS', 'STRINGS', 'BRASS', 'WOODWIND', 'SYNTH', 'DRUMS', 'PERCUSSION', 'OTHER'];

const SESSION_TYPE_ICONS = {
    PIANO: PianoIcon,
    GUITAR: GuitarIcon,
    BASS: BassIcon,
    STRINGS: StringsIcon,
    BRASS: BrassIcon,
    WOODWIND: WoodwindIcon,
    SYNTH: SynthIcon,
    DRUMS: DrumsIcon,
    PERCUSSION: PercussionIcon,
    OTHER: OtherInstrumentIcon,
};

const RecList = () => {
    const { projectId } = useParams();
    const myStudioLeftPanel = useStoreValue('myStudioLeftPanel');
    const recSessions = useStoreValue('recSessions');
    const selectedRecItemId = useStoreValue('selectedRecItemId');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showAddSessionFor, setShowAddSessionFor] = useState(null); // recItemId
    const [uploadTrackModal, setUploadTrackModal] = useState(null); // { recSessionId, type, sessionType }

    const handleSelectItem = (id) => {
        storeSet('selectedRecItemId', id);
        setShowAddSessionFor(null);
    };

    const handleAddSessionType = (recItemId, sessionType) => {
        setUploadTrackModal({ recSessionId: recItemId, type: 'AUX', sessionType });
        setShowAddSessionFor(null);
    };

    const handleUpdateAuxLayerAssignee = (recItemId, auxLayerId, user) => {
        const updatedSessions = recSessions.map(s => {
            if (s.id !== recItemId) return s;
            return {
                ...s,
                auxLayers: s.auxLayers.map(layer =>
                    layer.id === auxLayerId
                        ? { ...layer, assignedUser: user }
                        : layer
                )
            };
        });
        storeSet('recSessions', updatedSessions);
    };

    const handleDeleteAuxLayer = (recItemId, auxLayerId) => {
        const updatedSessions = recSessions.map(s =>
            s.id !== recItemId ? s :
            { ...s, auxLayers: s.auxLayers.filter(l => l.id !== auxLayerId) }
        );
        storeSet('recSessions', updatedSessions);
    };

    const handleUpdateAuxLayerVolume = (recItemId, auxLayerId, value) => {
        const updatedSessions = recSessions.map(s => {
            if (s.id !== recItemId) return s;
            return { ...s, auxLayers: s.auxLayers.map(l =>
                l.id === auxLayerId ? { ...l, volume: value } : l
            )};
        });
        storeSet('recSessions', updatedSessions);
    };

    return (
        myStudioLeftPanel === 'rec'
            ? <>
                <OverlayScrollbarsComponent element="div" options={{ scrollbars: { autoHide: 'never' } }} defer>
                    <ul className="timelineEditorAuxControls__listOfTracks">
                        {recSessions && !isEmpty(recSessions)
                            ? recSessions.map(item => {
                                const isSelected = item.id === selectedRecItemId;
                                return (
                                    <li
                                        key={item.id}
                                        className={`recList__item${isSelected ? ' recList__item--selected' : ''}`}
                                        onClick={() => handleSelectItem(item.id)}
                                    >
                                        <div className="recList__itemRow">
                                            <div>
                                                <BadgeTrack>{item.label}</BadgeTrack>
                                            </div>
                                            <div className="title">
                                                <h3>{item.title}</h3>
                                                <span>{item.timecode}</span>
                                            </div>
                                            <div className="rating lastGroup">
                                                <ButtonIconSimple onClick={e => e.stopPropagation()}>
                                                    <LikeIcon/>
                                                </ButtonIconSimple>
                                                <ButtonIconSimple onClick={e => e.stopPropagation()}>
                                                    <ToReviewIcon/>
                                                </ButtonIconSimple>
                                                <ButtonIconSimple onClick={e => e.stopPropagation()}>
                                                    <DislikeIcon/>
                                                </ButtonIconSimple>
                                            </div>
                                            <div className="meta">
                                                <span>{getDateFromISOstring(item.date, {
                                                    day: '2-digit',
                                                    month: 'short'
                                                })}</span>
                                                <ButtonIconContextMenu onClick={e => e.stopPropagation()}>
                                                    <TripleDotsIcon/>
                                                </ButtonIconContextMenu>
                                            </div>
                                        </div>

                                        {isSelected && (
                                            <div
                                                className="recList__auxLayers"
                                                onClick={e => e.stopPropagation()}
                                            >
                                                {item.auxLayers.map(layer => (
                                                    <div key={layer.id} className="recList__auxLayer">
                                                        <div className="recList__auxLayer__topRow">
                                                            <div className="recList__auxLayer__left">
                                                                <div className="recList__auxLayer__icon">
                                                                    {React.createElement(SESSION_TYPE_ICONS[layer.sessionType] ?? OtherInstrumentIcon)}
                                                                </div>
                                                                <RecLayerAssignButton
                                                                    assignedUser={layer.assignedUser}
                                                                    onChange={(user) => handleUpdateAuxLayerAssignee(item.id, layer.id, user)}
                                                                />
                                                            </div>
                                                            <div className="rating lastGroup">
                                                                <ButtonIconSimple onClick={e => e.stopPropagation()}>
                                                                    <LikeIcon/>
                                                                </ButtonIconSimple>
                                                                <ButtonIconSimple onClick={e => e.stopPropagation()}>
                                                                    <ToReviewIcon/>
                                                                </ButtonIconSimple>
                                                                <ButtonIconSimple onClick={e => e.stopPropagation()}>
                                                                    <DislikeIcon/>
                                                                </ButtonIconSimple>
                                                            </div>
                                                            <div className="recList__auxLayer__right">
                                                                <span className="recList__auxLayer__date">
                                                                    {getDateFromISOstring(item.date, { day: '2-digit', month: 'short' })}
                                                                </span>
                                                                <ButtonIconContextMenu
                                                                    menuItems={[{ label: __('Cancella', 'soundbetween'), onClick: () => handleDeleteAuxLayer(item.id, layer.id) }]}
                                                                >
                                                                    <TripleDotsIcon/>
                                                                </ButtonIconContextMenu>
                                                            </div>
                                                        </div>
                                                        <div className="recList__auxLayer__volume">
                                                            <span className="recList__auxLayer__volumeIcon"><VolumeIcon/></span>
                                                            <input
                                                                type="range"
                                                                className="recList__auxLayer__volumeSlider"
                                                                min={0} max={100} step={1}
                                                                value={layer.volume ?? 0}
                                                                onChange={(e) => handleUpdateAuxLayerVolume(item.id, layer.id, Number(e.target.value))}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}

                                                {(!item.coreLayer || !item.coreLayer.tracks?.length) && (
                                                    <button
                                                        type="button"
                                                        className="recList__addMainTrack"
                                                        onClick={() => setUploadTrackModal({ recSessionId: item.id, type: 'CORE', sessionType: null })}
                                                    >
                                                        {__('+ Aggiungi traccia principale', 'soundbetween')}
                                                    </button>
                                                )}

                                                <button
                                                    type="button"
                                                    className="recList__addSession"
                                                    onClick={() => setShowAddSessionFor(
                                                        showAddSessionFor === item.id ? null : item.id
                                                    )}
                                                >
                                                    {__('+ Aggiungi sessione remota', 'soundbetween')}
                                                </button>

                                                {showAddSessionFor === item.id && (
                                                    <div className="recList__sessionTypes">
                                                        {SESSION_TYPES.map(type => (
                                                            <button
                                                                key={type}
                                                                type="button"
                                                                className="recList__sessionTypeBtn"
                                                                onClick={() => handleAddSessionType(item.id, type)}
                                                            >
                                                                {type}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </li>
                                );
                            }) : null}
                    </ul>
                </OverlayScrollbarsComponent>
                <button className="timelineEditorAuxControls__addNew" onClick={() => setShowUploadModal(true)}>
                    <PlusIcon/>
                </button>
                {showUploadModal && (
                    <ModalUploadSimple
                        projectId={projectId}
                        type="rec"
                        headerText="Upload Recording"
                        onClose={() => setShowUploadModal(false)}
                    />
                )}
                {uploadTrackModal && (
                    <ModalUploadTrack
                        projectId={projectId}
                        recSessionId={uploadTrackModal.recSessionId}
                        type={uploadTrackModal.type}
                        sessionType={uploadTrackModal.sessionType}
                        onClose={() => setUploadTrackModal(null)}
                    />
                )}
            </> : null
    );
};

export default RecList;
